import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { DOCUMENT } from '@angular/common';
import { ApplicationContextService } from './application-context.service';
import { BaseSignalrService } from './signalr.service';

export interface CommitMessageSuggestion {
  suggestionId: number;
  title: string;
  description: string;
}

export interface CodeReviewResult {
  suggestionId: number;
  codeReviewMarkdown: string;
}

export interface ReleaseNotesResult {
  suggestionId: number;
  technicalNotes: string;
  customerNotes: string;
}

export enum SideKickAction {
  CommitTitle = 'CommitTitle',
  CodeReview = 'CodeReview',
  ReleaseNotes = 'ReleaseNotes'
}

@Injectable({
  providedIn: 'root'
})
export class SideKickSignalrService extends BaseSignalrService {
  private _endpoint = new BehaviorSubject<string>(null);

  private _commitMessageSuggestionCompleted = new Subject<CommitMessageSuggestion>();
  private _codeReviewCompleted = new Subject<CodeReviewResult>();
  private _releaseNotesCompleted = new Subject<ReleaseNotesResult>();

  public commitMessageSuggestionCompleted$ = this._commitMessageSuggestionCompleted.asObservable();
  public codeReviewCompleted$ = this._codeReviewCompleted.asObservable();
  public releaseNotesCompleted$ = this._releaseNotesCompleted.asObservable();

  constructor(
    applicationContextService: ApplicationContextService,
    authService: MsalService,
    @Inject(DOCUMENT) document: Document
  ) {
    super(applicationContextService, authService, document);
    super.init();
  }

  protected getEndpoint(): Observable<string> {
    return this._endpoint.asObservable();
  }

  protected registerListeners(register: <T>(method: string) => Observable<T>): void {
    register<CommitMessageSuggestion>('commitMessageSuggestionCompleted')
      .subscribe(data => this._commitMessageSuggestionCompleted.next(data));

    register<CodeReviewResult>('codeReviewCompleted')
      .subscribe(data => this._codeReviewCompleted.next(data));

    register<ReleaseNotesResult>('releaseNotesCompleted')
      .subscribe(data => this._releaseNotesCompleted.next(data));
  }

  protected connectionStarted(_disconnected: Subject<void>): void {
    // No additional setup needed for on-demand connections
  }

  connectCommitMessageSuggestion(applicationId: number): void {
    this._endpoint.next(`/sideKickHub?action=${SideKickAction.CommitTitle}&applicationId=${applicationId}`);
  }

  connectCodeReview(applicationId: number): void {
    this._endpoint.next(`/sideKickHub?action=${SideKickAction.CodeReview}&applicationId=${applicationId}`);
  }

  connectReleaseNotes(fromVersion: number, toVersion: number): void {
    this._endpoint.next(`/sideKickHub?action=${SideKickAction.ReleaseNotes}&fromVersion=${fromVersion}&toVersion=${toVersion}`);
  }

  disconnect(): void {
    this._endpoint.next(null);
  }
}
